import type { WorkbenchSaveMetadata, WorkbenchSavePayload } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { dscWorkbenchApi } from "../../../api/client";
import { generateIdempotencyKey } from "../../../lib/idempotencyKey";
import { useForceExitStore } from "../../../stores/forceExitStore";
import { worknumberKeys } from "../sections/Signers/api/worknumberKeys";

export interface SaveWorkbenchInput {
  approvalId: string;
  payload: WorkbenchSavePayload;
}

export interface SaveWorkbenchResult {
  metadata: WorkbenchSaveMetadata | undefined;
  /**
   * Raw HTTP status of the save response — any 2xx resolves here (axios
   * only rejects on non-2xx), but the footer's success toast should only
   * fire for an exact 200, so callers need the real code, not just success.
   */
  status: number;
}

/**
 * Mutation hook backing the single aggregate Workbench save endpoint (POST
 * /api/v1/workbench/applications/{approvalId}). Every registered section
 * (Signers today; more to come) contributes its own leg of `payload` via the
 * save orchestrator (see useSaveAllSections); this hook only owns what's
 * common to every save: the force-exit guard, the per-attempt
 * `Idempotency-Key`, and the Work Number cache invalidation: a just-saved
 * Income Type change (e.g. switching to Employee/W2) needs a fresh
 * eligibility read rather than the pre-save `canPull: false` still sitting in
 * cache (OR-23450). Dashboard and stipulations invalidation are sequenced by
 * the caller instead (see useSaveAllSections), relative to the
 * pre-approval-advance follow-up call, rather than firing in parallel here.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveWorkbench() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ approvalId, payload }: SaveWorkbenchInput): Promise<SaveWorkbenchResult> => {
      // Read live store state (not a subscribed hook value) so a force-exit
      // flag set moments earlier by ReviewPage's click-time recheck is seen
      // even though this component hasn't re-rendered yet.
      if (useForceExitStore.getState().shouldForceExit) {
        throw new Error(t("workbench.saveAbortedForceExit"));
      }

      const idempotencyKey = generateIdempotencyKey();
      const response = await dscWorkbenchApi.save(approvalId, payload, idempotencyKey);

      return { metadata: response.data.metadata, status: response.status };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: worknumberKeys.all });
    },
  });
}
